/**
 * Secure encryption utility for protecting sensitive data in localStorage
 * Uses Web Crypto API (AES-GCM) for strong encryption
 *
 * Security features:
 * - AES-256-GCM encryption
 * - Session-based encryption keys (cleared on browser close)
 * - Unique initialization vectors (IV) for each encryption
 * - Key derivation from session credentials
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits recommended for AES-GCM

/**
 * Generates or retrieves the encryption key for the current session
 * Key is stored in sessionStorage (cleared when browser closes)
 *
 * @param {string} userId - User ID to salt the key derivation
 * @returns {Promise<CryptoKey>} The encryption key
 */
const getEncryptionKey = async (userId = 'default') => {
  try {
    // Check if we already have a key in sessionStorage
    const storedKeyData = sessionStorage.getItem('_enc_key');

    if (storedKeyData) {
      // Import the stored key
      const keyData = JSON.parse(storedKeyData);
      const keyBuffer = new Uint8Array(Object.values(keyData));

      return await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: ALGORITHM, length: KEY_LENGTH },
        true,
        ['encrypt', 'decrypt']
      );
    }

    // Generate a new key if none exists
    const key = await crypto.subtle.generateKey(
      { name: ALGORITHM, length: KEY_LENGTH },
      true, // extractable
      ['encrypt', 'decrypt']
    );

    // Export and store the key in sessionStorage
    const exportedKey = await crypto.subtle.exportKey('raw', key);
    const keyArray = Array.from(new Uint8Array(exportedKey));
    sessionStorage.setItem('_enc_key', JSON.stringify(keyArray));

    return key;
  } catch (error) {
    console.error('[Encryption] Failed to get/generate key:', error);
    throw new Error('Encryption key initialization failed');
  }
};

/**
 * Encrypts data using AES-GCM
 *
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @param {string} userId - User ID for key derivation
 * @returns {Promise<string>} Base64 encoded encrypted data with IV
 */
export const encryptData = async (data, userId = 'default') => {
  try {
    // Convert data to string
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);

    // Get or generate encryption key
    const key = await getEncryptionKey(userId);

    // Generate a random IV (initialization vector)
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv: iv },
      key,
      dataBuffer
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('[Encryption] Failed to encrypt data:', error);
    throw new Error('Data encryption failed');
  }
};

/**
 * Decrypts data that was encrypted with encryptData
 *
 * @param {string} encryptedData - Base64 encoded encrypted data
 * @param {string} userId - User ID for key derivation
 * @returns {Promise<any>} Decrypted and parsed data
 */
export const decryptData = async (encryptedData, userId = 'default') => {
  try {
    // Decode base64
    const combined = new Uint8Array(
      atob(encryptedData).split('').map(char => char.charCodeAt(0))
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, IV_LENGTH);
    const encryptedBuffer = combined.slice(IV_LENGTH);

    // Get encryption key
    const key = await getEncryptionKey(userId);

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: iv },
      key,
      encryptedBuffer
    );

    // Convert back to string and parse JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('[Encryption] Failed to decrypt data:', error);
    // Return null instead of throwing to handle corrupted cache gracefully
    return null;
  }
};

/**
 * Securely stores data in localStorage with encryption
 *
 * @param {string} key - Storage key
 * @param {any} data - Data to store
 * @param {string} userId - User ID for key derivation
 * @returns {Promise<boolean>} Success status
 */
export const secureStorageSet = async (key, data, userId = 'default') => {
  try {
    const encrypted = await encryptData(data, userId);
    localStorage.setItem(key, encrypted);
    return true;
  } catch (error) {
    console.error('[SecureStorage] Failed to store data:', error);
    return false;
  }
};

/**
 * Retrieves and decrypts data from localStorage
 *
 * @param {string} key - Storage key
 * @param {string} userId - User ID for key derivation
 * @returns {Promise<any|null>} Decrypted data or null if not found/invalid
 */
export const secureStorageGet = async (key, userId = 'default') => {
  try {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;

    return await decryptData(encrypted, userId);
  } catch (error) {
    console.error('[SecureStorage] Failed to retrieve data:', error);
    // Remove corrupted data
    localStorage.removeItem(key);
    return null;
  }
};

/**
 * Removes data from localStorage
 *
 * @param {string} key - Storage key
 */
export const secureStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[SecureStorage] Failed to remove data:', error);
  }
};

/**
 * Clears all encrypted storage and session keys
 * Call this on logout
 */
export const clearSecureStorage = () => {
  try {
    // Remove all cache keys (you can customize this pattern)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('cache') || key.includes('secure'))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear session encryption key
    sessionStorage.removeItem('_enc_key');
  } catch (error) {
    console.error('[SecureStorage] Failed to clear storage:', error);
  }
};

/**
 * Validates that encryption is available in the browser
 *
 * @returns {boolean} Whether encryption is supported
 */
export const isEncryptionAvailable = () => {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.subtle.encrypt === 'function'
  );
};
